import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { 
  Users, Search, LogOut, Phone, Archive, CheckCircle2, 
  X, Check, Mail, MapPin, GraduationCap, Car, Briefcase, Plus, Euro, Edit2, Menu, Tag, Settings, Trash2, Send, FileText, Shield, RotateCcw, BarChart3, TrendingUp, Clock, Calendar, Target, UserCheck, XCircle, Activity, Copy, ArrowLeft
} from 'lucide-react';
import { AUSTRIAN_STATES, getDistrictsForState, getGemeindenForDistrict } from '../data/austrianLocations';
import { JobForm } from '../components/admin/JobForm';
import { ArchivedUsersList } from '../components/admin/ArchivedUsersList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user, onLogout }) {
  // Prevent search engine indexing of admin area
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  const [applicants, setApplicants] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [activeView, setActiveView] = useState('applicants'); // applicants, jobs, archived-users, archived-jobs, settings, statistics
  const [jobs, setJobs] = useState([]);
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [showEditJobDialog, setShowEditJobDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settingsCategoryInput, setSettingsCategoryInput] = useState('');
  const [smtpSettings, setSmtpSettings] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    admin_email: ''
  });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState({
    registration: { subject: '', body: '' },
    new_application: { subject: '', body: '' },
    application_accepted: { subject: '', body: '' },
    application_rejected: { subject: '', body: '' }
  });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newJob, setNewJob] = useState({
    title: '',
    description: '',
    location: '',
    state: 'Wien',
    district: '',
    gemeinde: '',
    salaryBrutto: '',
    hoursPerWeek: '20',
    workingHours: '',
    employmentType: 'Teilzeit',
    categories: [],
    adminNote: ''  // Internal note visible only to admins (e.g. company name)
  });
  const noteTimeoutRef = useRef(null);
  
  // Statistics state
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState('all');

  // Propose-Job dialog state
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeApplicant, setProposeApplicant] = useState(null);
  const [proposeJobId, setProposeJobId] = useState('');
  const [proposeMessage, setProposeMessage] = useState('');
  const [proposeLoading, setProposeLoading] = useState(false);

  useEffect(() => {
    fetchApplicants();
    fetchJobs();
    fetchArchivedData();
    fetchCategories();
    fetchSmtpSettings();
    fetchEmailTemplates();
  }, []);

  // Refresh archived data when switching to archive views
  useEffect(() => {
    if (activeView === 'archived-users' || activeView === 'archived-jobs') {
      fetchArchivedData();
    }
    if (activeView === 'applicants') {
      fetchApplicants();
    }
    if (activeView === 'jobs') {
      fetchJobs();
    }
  }, [activeView]);

  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/email-templates`);
      if (response.data.templates) {
        setEmailTemplates(response.data.templates);
      }
    } catch (error) {
      console.error('Error fetching email templates');
    }
  };

  const handleSaveEmailTemplate = async (key) => {
    try {
      await axios.post(`${API}/admin/settings/email-templates`, { templates: emailTemplates });
      toast.success('E-Mail-Vorlage gespeichert');
      setEditingTemplate(null);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const fetchSmtpSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/smtp`);
      if (response.data.configured) {
        setSmtpSettings({
          host: response.data.host,
          port: response.data.port,
          username: response.data.username,
          password: '', // Don't show password
          from_email: response.data.from_email,
          admin_email: response.data.admin_email
        });
        setSmtpConfigured(true);
      }
    } catch (error) {
      console.error('Error fetching SMTP settings');
    }
  };

  const handleSaveSmtpSettings = async () => {
    try {
      await axios.post(`${API}/admin/settings/smtp`, smtpSettings);
      toast.success('SMTP-Einstellungen gespeichert');
      setSmtpConfigured(true);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const response = await axios.post(`${API}/admin/settings/smtp/test`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP-Test fehlgeschlagen');
    } finally {
      setTestingSmtp(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/job-categories`);
      setAvailableCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories');
    }
  };

  const fetchArchivedData = async () => {
    try {
      const [jobsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/jobs/archived`),
        axios.get(`${API}/admin/users/archived`)
      ]);
      setArchivedJobs(jobsRes.data);
      setArchivedUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching archived data');
    }
  };

  const fetchJobs = async () => {
    try {
      // Use admin endpoint to get jobs with adminNote
      const response = await axios.get(`${API}/admin/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs');
    }
  };

  const handleCreateJob = async () => {
    try {
      await axios.post(`${API}/admin/jobs`, {
        ...newJob,
        salaryBrutto: parseInt(newJob.salaryBrutto) || 0,
        hoursPerWeek: parseInt(newJob.hoursPerWeek) || 20,
        categories: newJob.categories,
        adminNote: newJob.adminNote
      });
      toast.success('Job erstellt!');
      setShowJobDialog(false);
      setNewJob({
        title: '',
        description: '',
        location: '',
        state: 'Wien',
        district: '',
        gemeinde: '',
        salaryBrutto: '',
        hoursPerWeek: '20',
        workingHours: '',
        employmentType: 'Teilzeit',
        categories: [],
        adminNote: ''
      });
      setNewCategoryInput('');
      fetchJobs();
      fetchCategories();
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleEditJob = (job) => {
    setEditingJob({
      ...job,
      salaryBrutto: job.salaryBrutto || job.monthlySalary || '',
      workingHours: job.workingHours || '',
      categories: job.categories || [],
      gemeinde: job.gemeinde || '',
      adminNote: job.adminNote || ''
    });
    setShowEditJobDialog(true);
  };

  const handleUpdateJob = async () => {
    try {
      await axios.patch(`${API}/admin/jobs/${editingJob.jobId}`, {
        title: editingJob.title,
        description: editingJob.description,
        location: editingJob.location,
        state: editingJob.state,
        district: editingJob.district,
        gemeinde: editingJob.gemeinde || '',
        salaryBrutto: parseInt(editingJob.salaryBrutto) || 0,
        hoursPerWeek: parseInt(editingJob.hoursPerWeek) || 20,
        workingHours: editingJob.workingHours,
        employmentType: editingJob.employmentType,
        categories: editingJob.categories,
        adminNote: editingJob.adminNote
      });
      toast.success('Job aktualisiert!');
      setShowEditJobDialog(false);
      setEditingJob(null);
      setNewCategoryInput('');
      fetchJobs();
      fetchCategories();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Category toggling and addition is handled inside <JobForm /> component.

  const handleArchiveJob = async (jobId) => {
    try {
      await axios.patch(`${API}/admin/jobs/${jobId}/archive`);
      toast.success('Job archiviert');
      fetchJobs();
      fetchArchivedData();
    } catch (error) {
      toast.error('Fehler beim Archivieren');
    }
  };

  const handleCopyJob = async (jobId) => {
    try {
      await axios.post(`${API}/admin/jobs/${jobId}/copy`);
      toast.success('Job kopiert!');
      fetchJobs();
    } catch (error) {
      toast.error('Fehler beim Kopieren');
    }
  };

  const handleDeleteJob = async (jobId, jobTitle) => {
    if (!window.confirm(`Job "${jobTitle}" wirklich ENDGÜLTIG löschen?\n\nAlle zugehörigen Bewerbungen werden ebenfalls gelöscht!`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`);
      toast.success('Job gelöscht');
      fetchJobs();
      fetchArchivedData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleReactivateJob = async (jobId) => {
    try {
      await axios.patch(`${API}/admin/jobs/${jobId}/reactivate`);
      toast.success('Job wieder aktiviert!');
      fetchJobs();
      fetchArchivedData();
    } catch (error) {
      toast.error('Fehler beim Reaktivieren');
    }
  };

  const handleUnarchiveUser = async (uid) => {
    try {
      await axios.patch(`${API}/admin/users/${uid}/unarchive`);
      toast.success('Bewerber wiederhergestellt');
      fetchApplicants();
      fetchArchivedData();
    } catch (error) {
      toast.error('Fehler beim Wiederherstellen');
    }
  };

  const handleDeleteUser = async (uid, displayName) => {
    if (!window.confirm(`Bewerber "${displayName}" wirklich ENDGÜLTIG löschen?\n\nDieser Vorgang kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/users/${uid}`);
      toast.success('Bewerber gelöscht');
      fetchArchivedData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const openProposeJobDialog = (applicant) => {
    setProposeApplicant(applicant);
    setProposeJobId('');
    setProposeMessage('');
    setProposeOpen(true);
  };

  const handleSendProposal = async () => {
    if (!proposeApplicant || !proposeJobId) {
      toast.error('Bitte einen Job auswählen');
      return;
    }
    setProposeLoading(true);
    try {
      await axios.post(`${API}/admin/proposals`, {
        jobId: proposeJobId,
        userId: proposeApplicant.uid,
        message: proposeMessage || null,
      });
      toast.success('Job-Vorschlag wurde versendet 📧');
      setProposeOpen(false);
      setProposeApplicant(null);
      setProposeJobId('');
      setProposeMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Versenden');
    } finally {
      setProposeLoading(false);
    }
  };

  const handleDownloadSteckbrief = async (applicant) => {
    try {
      const response = await axios.get(
        `${API}/admin/applicants/${applicant.uid}/steckbrief`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (applicant.profile?.firstName || 'Kandidat').replace(/[^a-zA-Z0-9_-]/g, '');
      link.download = `steckbrief_${safeName}_${applicant.uid.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Steckbrief-PDF heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Generieren des PDFs');
    }
  };

  // Category Management Functions
  const handleAddMasterCategory = async () => {
    const trimmed = settingsCategoryInput.trim();
    if (!trimmed) return;
    
    try {
      const response = await axios.post(`${API}/admin/categories`, { category: trimmed });
      setAvailableCategories(response.data.categories);
      setSettingsCategoryInput('');
      toast.success('Kategorie hinzugefügt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  };

  const handleDeleteMasterCategory = async (category) => {
    try {
      const response = await axios.delete(`${API}/admin/categories/${encodeURIComponent(category)}`);
      setAvailableCategories(response.data.categories);
      toast.success('Kategorie gelöscht');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const fetchStatistics = async (period = statsPeriod) => {
    setStatsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/statistics?period=${period}`);
      setStatistics(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchApplicants = async () => {
    try {
      const response = await axios.get(`${API}/admin/applicants`);
      setApplicants(response.data);
      if (response.data.length > 0 && !selectedApplicant) {
        setSelectedApplicant(response.data[0]);
        setNoteText(response.data[0].adminNote || '');
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Bewerber');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectApplicant = async (applicant) => {
    setSelectedApplicant(applicant);
    setNoteText(applicant.adminNote || '');
    
    // Markiere als "angesehen" wenn noch nicht angesehen (NEU-Tag verschwindet)
    if (!applicant.viewed) {
      try {
        await axios.patch(`${API}/admin/applicants/${applicant.uid}/status`, {
          field: 'viewed',
          value: true
        });
        // Lokalen State aktualisieren
        setApplicants(prev => prev.map(a => 
          a.uid === applicant.uid ? { ...a, viewed: true } : a
        ));
        setSelectedApplicant(prev => ({ ...prev, viewed: true }));
      } catch (error) {
        console.error('Fehler beim Aktualisieren des Status');
      }
    }
  };

  const handleNoteChange = (value) => {
    setNoteText(value);
    
    // Auto-save after 1 second of no typing
    if (noteTimeoutRef.current) {
      clearTimeout(noteTimeoutRef.current);
    }
    
    noteTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.patch(`${API}/admin/applicants/${selectedApplicant.uid}/note`, {
          adminNote: value
        });
        toast.success('Notiz gespeichert', { duration: 1500 });
        fetchApplicants();
      } catch (error) {
        toast.error('Fehler beim Speichern der Notiz');
      }
    }, 1000);
  };

  const handleStatusToggle = async (field, currentValue) => {
    try {
      await axios.patch(`${API}/admin/applicants/${selectedApplicant.uid}/status`, {
        field,
        value: !currentValue
      });
      toast.success('Status aktualisiert');
      fetchApplicants();
      setSelectedApplicant(prev => ({ ...prev, [field]: !currentValue }));
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Status');
    }
  };

  const handleApplicationStatus = async (appId, status) => {
    try {
      await axios.patch(`${API}/admin/applications/${appId}/status`, { status });
      toast.success(`Bewerbung ${status === 'zugesagt' ? 'zugesagt' : 'abgelehnt'}`);
      fetchApplicants();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Bewerbung');
    }
  };

  const handleProfileUpdate = async (field, value) => {
    try {
      await axios.patch(`${API}/admin/applicants/${selectedApplicant.uid}/profile`, {
        [field]: value
      });
      toast.success('Profil aktualisiert');
      fetchApplicants();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const filteredApplicants = applicants.filter(applicant => {
    const searchLower = searchTerm.toLowerCase();
    return (
      applicant.profile?.firstName?.toLowerCase().includes(searchLower) ||
      applicant.profile?.lastName?.toLowerCase().includes(searchLower) ||
      applicant.email?.toLowerCase().includes(searchLower) ||
      applicant.profile?.study?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="UniSide" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="text-lg md:text-xl font-bold font-outfit">UniSide Admin</h1>
              <p className="text-xs md:text-sm text-slate-400 hidden sm:block">{user.email}</p>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4">
            <Button
              onClick={() => setActiveView('applicants')}
              variant={activeView === 'applicants' ? 'default' : 'ghost'}
              className={activeView === 'applicants' ? '' : 'text-white hover:bg-slate-800'}
              data-testid="nav-applicants"
            >
              <Users className="w-4 h-4 mr-2" />
              Bewerber
            </Button>
            <Button
              onClick={() => setActiveView('jobs')}
              variant={activeView === 'jobs' ? 'default' : 'ghost'}
              className={activeView === 'jobs' ? '' : 'text-white hover:bg-slate-800'}
              data-testid="nav-jobs"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Jobs
            </Button>
            <Button
              onClick={() => setActiveView('archived-users')}
              variant={activeView === 'archived-users' ? 'default' : 'ghost'}
              className={activeView === 'archived-users' ? '' : 'text-white hover:bg-slate-800'}
              data-testid="nav-archived-users"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archiv (User)
            </Button>
            <Button
              onClick={() => setActiveView('archived-jobs')}
              variant={activeView === 'archived-jobs' ? 'default' : 'ghost'}
              className={activeView === 'archived-jobs' ? '' : 'text-white hover:bg-slate-800'}
              data-testid="nav-archived-jobs"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archiv (Jobs)
            </Button>
            <Button
              onClick={() => { setActiveView('statistics'); fetchStatistics(); }}
              variant={activeView === 'statistics' ? 'default' : 'ghost'}
              className={activeView === 'statistics' ? '' : 'text-white hover:bg-slate-800'}
              data-testid="nav-statistics"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Statistiken
            </Button>
            <Button
              onClick={() => setActiveView('settings')}
              variant={activeView === 'settings' ? 'default' : 'ghost'}
              className={activeView === 'settings' ? '' : 'text-white hover:bg-slate-800'}
              data-testid="nav-settings"
            >
              <Settings className="w-4 h-4 mr-2" />
              Einstellungen
            </Button>
            <Button
              onClick={onLogout}
              variant="ghost"
              className="text-white hover:bg-slate-800"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Abmelden
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-white hover:bg-slate-800"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </Button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-700 px-4 py-3 space-y-2">
            <Button
              onClick={() => { setActiveView('applicants'); setMobileMenuOpen(false); }}
              variant={activeView === 'applicants' ? 'default' : 'ghost'}
              className={`w-full justify-start ${activeView === 'applicants' ? '' : 'text-white hover:bg-slate-800'}`}
            >
              <Users className="w-4 h-4 mr-2" />
              Bewerber
            </Button>
            <Button
              onClick={() => { setActiveView('jobs'); setMobileMenuOpen(false); }}
              variant={activeView === 'jobs' ? 'default' : 'ghost'}
              className={`w-full justify-start ${activeView === 'jobs' ? '' : 'text-white hover:bg-slate-800'}`}
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Jobs
            </Button>
            <Button
              onClick={() => { setActiveView('archived-users'); setMobileMenuOpen(false); }}
              variant={activeView === 'archived-users' ? 'default' : 'ghost'}
              className={`w-full justify-start ${activeView === 'archived-users' ? '' : 'text-white hover:bg-slate-800'}`}
            >
              <Archive className="w-4 h-4 mr-2" />
              Archiv (User)
            </Button>
            <Button
              onClick={() => { setActiveView('archived-jobs'); setMobileMenuOpen(false); }}
              variant={activeView === 'archived-jobs' ? 'default' : 'ghost'}
              className={`w-full justify-start ${activeView === 'archived-jobs' ? '' : 'text-white hover:bg-slate-800'}`}
            >
              <Archive className="w-4 h-4 mr-2" />
              Archiv (Jobs)
            </Button>
            <Button
              onClick={() => { setActiveView('statistics'); fetchStatistics(); setMobileMenuOpen(false); }}
              variant={activeView === 'statistics' ? 'default' : 'ghost'}
              className={`w-full justify-start ${activeView === 'statistics' ? '' : 'text-white hover:bg-slate-800'}`}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Statistiken
            </Button>
            <Button
              onClick={() => { setActiveView('settings'); setMobileMenuOpen(false); }}
              variant={activeView === 'settings' ? 'default' : 'ghost'}
              className={`w-full justify-start ${activeView === 'settings' ? '' : 'text-white hover:bg-slate-800'}`}
            >
              <Settings className="w-4 h-4 mr-2" />
              Einstellungen
            </Button>
            <Button
              onClick={onLogout}
              variant="ghost"
              className="w-full justify-start text-white hover:bg-slate-800"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Abmelden
            </Button>
          </div>
        )}
      </div>

      {/* Main Content - Split Screen */}
      {activeView === 'applicants' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 min-h-[calc(100vh-73px)] lg:h-[calc(100vh-73px)]">
        {/* Left Panel - Applicants List - hidden on mobile when applicant selected */}
        <div className={`lg:col-span-4 border-r border-slate-200 bg-white overflow-y-auto ${selectedApplicant ? 'hidden lg:block' : 'block'}`}>
          <div className="p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suche nach Name, Email, Studium..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10 rounded-xl"
                data-testid="search-applicants-input"
              />
            </div>
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-slate-600">{filteredApplicants.length} Bewerber</span>
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {filteredApplicants.map((applicant) => {
              const isNew = !applicant.viewed;
              const isNotContacted = !applicant.contacted;
              const hasApplications = applicant.applications?.length > 0;

              return (
                <button
                  key={applicant.uid}
                  onClick={() => handleSelectApplicant(applicant)}
                  className={`w-full text-left p-4 hover:bg-slate-50 transition-colors ${
                    selectedApplicant?.uid === applicant.uid ? 'bg-primary/5 border-l-4 border-primary' : ''
                  }`}
                  data-testid={`applicant-item-${applicant.uid}`}
                >
                  <div className="flex items-start gap-3">
                    {applicant.profile?.profileImage ? (
                      <img
                        src={applicant.profile.profileImage}
                        alt={applicant.profile.firstName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                        <Users className="w-6 h-6 text-slate-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-slate-900 truncate">
                          {applicant.profile?.firstName} {applicant.profile?.lastName}
                        </p>
                        {isNew && (
                          <Badge className="bg-blue-500 text-white text-xs">Neu</Badge>
                        )}
                        {isNotContacted && (
                          <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">Nicht kontaktiert</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 truncate">{applicant.profile?.study || 'Kein Studium'}</p>
                      {hasApplications && (
                        <p className="text-xs text-slate-400 mt-1">
                          {applicant.applications.length} Bewerbung(en)
                        </p>
                      )}
                    </div>
                    {applicant.placed && (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                </button>
              );
            })}

            {filteredApplicants.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                Keine Bewerber gefunden
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Applicant Details - hidden on mobile when no applicant selected */}
        <div className={`lg:col-span-8 overflow-y-auto bg-slate-50 p-4 md:p-6 ${selectedApplicant ? 'block' : 'hidden lg:block'}`}>
          {selectedApplicant ? (
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Mobile Back Button */}
              <Button
                onClick={() => setSelectedApplicant(null)}
                variant="outline"
                size="sm"
                className="lg:hidden rounded-full"
                data-testid="back-to-applicants-list"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück zur Liste
              </Button>
              {/* Profile Header */}
              <Card className="border-slate-100 shadow-sm rounded-2xl">
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6">
                    {selectedApplicant.profile?.profileImage ? (
                      <img
                        src={selectedApplicant.profile.profileImage}
                        alt={selectedApplicant.profile.firstName}
                        className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-4 border-primary/20 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <Users className="w-10 h-10 md:w-12 md:h-12 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1 w-full min-w-0">
                      <h2 className="text-xl md:text-2xl font-bold font-outfit mb-1 break-words">
                        {selectedApplicant.profile?.firstName} {selectedApplicant.profile?.lastName}
                      </h2>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <Badge variant="outline" className="flex items-center gap-1 max-w-full">
                          <Mail className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{selectedApplicant.email}</span>
                        </Badge>
                        {selectedApplicant.profile?.phone && (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Phone className="w-3 h-3 flex-shrink-0" />
                            {selectedApplicant.profile.phone}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant={selectedApplicant.contacted ? 'default' : 'outline'}
                          onClick={() => handleStatusToggle('contacted', selectedApplicant.contacted)}
                          className="rounded-full"
                          data-testid="toggle-contacted-button"
                        >
                          <Phone className="w-4 h-4 mr-1" />
                          {selectedApplicant.contacted ? 'Kontaktiert' : 'Als kontaktiert markieren'}
                        </Button>
                        <Button
                          size="sm"
                          variant={selectedApplicant.placed ? 'default' : 'outline'}
                          onClick={() => handleStatusToggle('placed', selectedApplicant.placed)}
                          className="rounded-full bg-green-600 hover:bg-green-700 text-white"
                          data-testid="toggle-placed-button"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          {selectedApplicant.placed ? 'Vermittelt ✓' : 'Als vermittelt markieren'}
                        </Button>
                        <Button
                          size="sm"
                          variant={selectedApplicant.archived ? 'default' : 'outline'}
                          onClick={() => handleStatusToggle('archived', selectedApplicant.archived)}
                          className="rounded-full"
                          data-testid="toggle-archived-button"
                        >
                          <Archive className="w-4 h-4 mr-1" />
                          {selectedApplicant.archived ? 'Archiviert' : 'Archivieren'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openProposeJobDialog(selectedApplicant)}
                          className="rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          data-testid="open-propose-job-dialog"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Job vorschlagen
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadSteckbrief(selectedApplicant)}
                          className="rounded-full"
                          data-testid="download-steckbrief-button"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Steckbrief PDF
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Profile Details */}
              <Card className="border-slate-100 shadow-sm rounded-2xl">
                <CardHeader>
                  <CardTitle className="font-outfit text-lg">Profil-Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-slate-500 mb-1 block">Studium</label>
                      <div className="flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{selectedApplicant.profile?.study || '-'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-slate-500 mb-1 block">Mobilität</label>
                      <div className="flex gap-2">
                        {selectedApplicant.profile?.hasDriverLicense && (
                          <Badge variant="outline">Führerschein</Badge>
                        )}
                        {selectedApplicant.profile?.hasOwnCar && (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Car className="w-3 h-3" />
                            PKW
                          </Badge>
                        )}
                        {!selectedApplicant.profile?.hasDriverLicense && !selectedApplicant.profile?.hasOwnCar && '-'}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-slate-500 mb-1 block">Bevorzugte Regionen</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedApplicant.profile?.preferredStates?.map(state => (
                        <Badge key={state} variant="secondary">
                          <MapPin className="w-3 h-3 mr-1" />
                          {state}
                        </Badge>
                      ))}
                      {selectedApplicant.profile?.preferredDistricts?.map(district => (
                        <Badge key={district} variant="outline">{district}</Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-slate-500 mb-1 block">Interessen</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedApplicant.profile?.preferredJobAreas?.map(interest => (
                        <Badge key={interest} className="bg-primary/10 text-primary">
                          {interest}
                        </Badge>
                      ))}
                      {(!selectedApplicant.profile?.preferredJobAreas || selectedApplicant.profile.preferredJobAreas.length === 0) && (
                        <span className="text-sm text-slate-400">Keine Interessen angegeben</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Admin Notes */}
              <Card className="border-slate-100 shadow-sm rounded-2xl">
                <CardHeader>
                  <CardTitle className="font-outfit text-lg">Interne Notizen</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={noteText}
                    onChange={(e) => handleNoteChange(e.target.value)}
                    placeholder="Notizen werden automatisch gespeichert..."
                    className="min-h-[120px] rounded-xl"
                    data-testid="admin-notes-textarea"
                  />
                  <p className="text-xs text-slate-400 mt-2">
                    💡 Notizen werden automatisch nach 1 Sekunde gespeichert
                  </p>
                </CardContent>
              </Card>

              {/* Applications */}
              <Card className="border-slate-100 shadow-sm rounded-2xl">
                <CardHeader>
                  <CardTitle className="font-outfit text-lg flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    Bewerbungen ({selectedApplicant.applications?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedApplicant.applications?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedApplicant.applications.map((app) => (
                        <div
                          key={app.applicationId}
                          className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                          data-testid={`application-${app.applicationId}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-slate-900 mb-1">
                                {app.job?.title}
                                {app.job?.isDemo && (
                                  <Badge variant="outline" className="ml-2 text-xs">DEMO</Badge>
                                )}
                              </h4>
                              <p className="text-sm text-slate-500">
                                Beworben am {new Date(app.appliedAt).toLocaleDateString('de-AT')}
                              </p>
                              
                              {/* Admin Note from Job (visible to admins) */}
                              {app.job?.adminNote && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">
                                  <div className="flex items-center gap-1 text-amber-700 text-xs font-medium">
                                    <Shield className="w-3 h-3" />
                                    Firma/Notiz: <span className="text-amber-800">{app.job.adminNote}</span>
                                  </div>
                                </div>
                              )}
                              
                              <Badge
                                className={`mt-2 ${
                                  app.status === 'zugesagt'
                                    ? 'bg-green-100 text-green-700'
                                    : app.status === 'abgelehnt'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}
                              >
                                {app.status === 'beworben' && 'In Bearbeitung'}
                                {app.status === 'zugesagt' && 'Zugesagt'}
                                {app.status === 'abgelehnt' && 'Abgelehnt'}
                              </Badge>
                            </div>
                            {app.status === 'beworben' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleApplicationStatus(app.applicationId, 'zugesagt')}
                                  className="bg-green-600 hover:bg-green-700 rounded-full"
                                  data-testid={`approve-app-${app.applicationId}`}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleApplicationStatus(app.applicationId, 'abgelehnt')}
                                  className="rounded-full"
                                  data-testid={`reject-app-${app.applicationId}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">Keine Bewerbungen vorhanden</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-slate-500">
                <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p>Wähle einen Bewerber aus der Liste</p>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Jobs Management View */}
      {activeView === 'jobs' && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold font-outfit">Job-Verwaltung</h2>
              <p className="text-slate-600 text-sm">{jobs.length} Jobs insgesamt</p>
            </div>
            <Dialog open={showJobDialog} onOpenChange={setShowJobDialog}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-primary hover:bg-primary/90 shadow-lg w-full sm:w-auto" data-testid="create-job-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Neuen Job erstellen
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
                <DialogHeader>
                  <DialogTitle>Neuen Job erstellen</DialogTitle>
                  <DialogDescription>Füge einen neuen Job zur Plattform hinzu</DialogDescription>
                </DialogHeader>
                <JobForm
                  value={newJob}
                  onChange={setNewJob}
                  availableCategories={availableCategories}
                  newCategoryInput={newCategoryInput}
                  setNewCategoryInput={setNewCategoryInput}
                  testIdPrefix="job"
                />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowJobDialog(false)}>Abbrechen</Button>
                  <Button onClick={handleCreateJob} data-testid="submit-job-button">Job erstellen</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Job Dialog */}
            <Dialog open={showEditJobDialog} onOpenChange={setShowEditJobDialog}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
                <DialogHeader>
                  <DialogTitle>Job bearbeiten</DialogTitle>
                  <DialogDescription>Ändere die Job-Details</DialogDescription>
                </DialogHeader>
                {editingJob && (
                  <JobForm
                    value={editingJob}
                    onChange={setEditingJob}
                    availableCategories={availableCategories}
                    newCategoryInput={newCategoryInput}
                    setNewCategoryInput={setNewCategoryInput}
                    testIdPrefix="edit-job"
                  />
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowEditJobDialog(false)}>Abbrechen</Button>
                  <Button onClick={handleUpdateJob}>Änderungen speichern</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map((job) => (
              <Card key={job.jobId} className="border-slate-100 shadow-sm rounded-2xl" data-testid={`job-card-admin-${job.jobId}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg font-outfit">{job.title}</CardTitle>
                      <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
                        <MapPin className="w-4 h-4" />
                        <span>{job.location}</span>
                      </div>
                      {/* Admin Note - prominent display */}
                      {job.adminNote && (
                        <div className="mt-2 px-2 py-1 bg-amber-100 border-l-4 border-amber-500 rounded-r">
                          <span className="text-xs font-semibold text-amber-800">📋 {job.adminNote}</span>
                        </div>
                      )}
                    </div>
                    {job.isDemo && (
                      <Badge variant="outline" className="text-xs">DEMO</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{job.description}</p>
                  
                  {/* Job Categories */}
                  {job.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {job.categories.slice(0, 3).map((cat) => (
                        <Badge key={cat} variant="outline" className="text-xs bg-primary/5 text-primary border-primary/20">
                          {cat}
                        </Badge>
                      ))}
                      {job.categories.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{job.categories.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Stunden/Woche:</span>
                      <span className="font-medium">{job.hoursPerWeek}h</span>
                    </div>
                    {job.workingHours && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Dienstzeiten:</span>
                        <span className="font-medium text-xs">{job.workingHours}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-primary font-semibold">
                        <Euro className="w-4 h-4" />
                        <span>€{job.salaryBrutto || job.monthlySalary} brutto</span>
                      </div>
                      <Badge variant="secondary">{job.employmentType}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={() => handleEditJob(job)}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      data-testid={`edit-job-${job.jobId}`}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Bearbeiten
                    </Button>
                    <Button
                      onClick={() => handleCopyJob(job.jobId)}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      data-testid={`copy-job-${job.jobId}`}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Kopieren
                    </Button>
                    <Button
                      onClick={() => handleArchiveJob(job.jobId)}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      data-testid={`archive-job-${job.jobId}`}
                    >
                      <Archive className="w-4 h-4 mr-1" />
                      Archivieren
                    </Button>
                    <Button
                      onClick={() => handleDeleteJob(job.jobId, job.title)}
                      variant="outline"
                      size="sm"
                      className="rounded-full text-red-600 hover:bg-red-50 hover:text-red-700"
                      data-testid={`delete-job-${job.jobId}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Löschen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {jobs.length === 0 && (
            <div className="text-center py-12">
              <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Noch keine Jobs erstellt</p>
            </div>
          )}
        </div>
      )}

      {/* Archived Users View */}
      {activeView === 'archived-users' && (
        <ArchivedUsersList
          users={archivedUsers}
          onUnarchive={handleUnarchiveUser}
          onDelete={handleDeleteUser}
        />
      )}

      {/* Archived Jobs View */}
      {activeView === 'archived-jobs' && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <h2 className="text-xl md:text-2xl font-bold font-outfit">Archivierte Jobs</h2>
            <p className="text-slate-600 text-sm">{archivedJobs.length} archivierte Jobs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {archivedJobs.map((job) => (
              <Card key={job.jobId} className="border-slate-100 shadow-sm rounded-2xl bg-slate-50">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg font-outfit">{job.title}</CardTitle>
                      <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
                        <MapPin className="w-4 h-4" />
                        <span>{job.location}</span>
                      </div>
                      {/* Admin Note - prominent display */}
                      {job.adminNote && (
                        <div className="mt-2 px-2 py-1 bg-amber-100 border-l-4 border-amber-500 rounded-r">
                          <span className="text-xs font-semibold text-amber-800">📋 {job.adminNote}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{job.description}</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Stunden/Woche:</span>
                      <span className="font-medium">{job.hoursPerWeek}h</span>
                    </div>
                    {job.workingHours && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Dienstzeiten:</span>
                        <span className="font-medium text-xs">{job.workingHours}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-primary font-semibold">
                        <Euro className="w-4 h-4" />
                        <span>€{job.salaryBrutto || job.monthlySalary} brutto</span>
                      </div>
                      <Badge variant="secondary">{job.employmentType}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => handleReactivateJob(job.jobId)}
                      variant="default"
                      size="sm"
                      className="flex-1 rounded-full bg-green-600 hover:bg-green-700"
                      data-testid={`reactivate-job-${job.jobId}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Aktivieren
                    </Button>
                    <Button
                      onClick={() => handleEditJob(job)}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDeleteJob(job.jobId, job.title)}
                      variant="outline"
                      size="sm"
                      className="rounded-full text-red-600 hover:bg-red-50"
                      data-testid={`delete-archived-job-${job.jobId}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {archivedJobs.length === 0 && (
            <div className="text-center py-12">
              <Archive className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Keine archivierten Jobs</p>
            </div>
          )}
        </div>
      )}

      {/* Statistics View */}
      {activeView === 'statistics' && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold font-outfit flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-primary" />
                Statistiken & Übersicht
              </h2>
              <p className="text-slate-600">Alle wichtigen Kennzahlen auf einen Blick</p>
            </div>
            <div className="flex gap-2">
              <Select value={statsPeriod} onValueChange={(value) => { setStatsPeriod(value); fetchStatistics(value); }}>
                <SelectTrigger className="w-40 rounded-xl" data-testid="stats-period-select">
                  <SelectValue placeholder="Zeitraum" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Diese Woche</SelectItem>
                  <SelectItem value="month">Dieser Monat</SelectItem>
                  <SelectItem value="year">Dieses Jahr</SelectItem>
                  <SelectItem value="all">Gesamt</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => fetchStatistics(statsPeriod)} className="rounded-xl">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {statsLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : statistics ? (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-slate-100 shadow-sm rounded-2xl overflow-hidden">
                  <div className="h-1 bg-blue-500"></div>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Bewerbungen</p>
                        <p className="text-3xl font-bold text-slate-900">{statistics.overview.totalApplications}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-blue-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-100 shadow-sm rounded-2xl overflow-hidden">
                  <div className="h-1 bg-green-500"></div>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Vermittlungen</p>
                        <p className="text-3xl font-bold text-green-600">{statistics.overview.successfulPlacements}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                        <UserCheck className="w-6 h-6 text-green-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-100 shadow-sm rounded-2xl overflow-hidden">
                  <div className="h-1 bg-purple-500"></div>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Aktive Jobs</p>
                        <p className="text-3xl font-bold text-slate-900">{statistics.overview.activeJobs}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                        <Briefcase className="w-6 h-6 text-purple-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-100 shadow-sm rounded-2xl overflow-hidden">
                  <div className="h-1 bg-amber-500"></div>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Studenten</p>
                        <p className="text-3xl font-bold text-slate-900">{statistics.overview.totalStudents}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                        <Users className="w-6 h-6 text-amber-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Second Row KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="border-slate-100 shadow-sm rounded-2xl">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{statistics.overview.availableHours}h</p>
                    <p className="text-xs text-slate-500">Verfügbare Stunden</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-100 shadow-sm rounded-2xl">
                  <CardContent className="p-4 text-center">
                    <Target className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{statistics.overview.totalJobsPosted}</p>
                    <p className="text-xs text-slate-500">Jobs Gesamt</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-100 shadow-sm rounded-2xl">
                  <CardContent className="p-4 text-center">
                    <Phone className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{statistics.overview.contacted}</p>
                    <p className="text-xs text-slate-500">Kontaktaufnahmen</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-100 shadow-sm rounded-2xl">
                  <CardContent className="p-4 text-center">
                    <XCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-600">{statistics.overview.rejections}</p>
                    <p className="text-xs text-slate-500">Absagen</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-100 shadow-sm rounded-2xl">
                  <CardContent className="p-4 text-center">
                    <Calendar className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm font-bold text-slate-900">
                      {statistics.overview.onlineSince ? new Date(statistics.overview.onlineSince).toLocaleDateString('de-AT') : '-'}
                    </p>
                    <p className="text-xs text-slate-500">Online seit</p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Applications Over Time Chart */}
                <Card className="border-slate-100 shadow-sm rounded-2xl">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-outfit flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      Bewerbungen über Zeit
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={statistics.applicationsOverTime}>
                          <defs>
                            <linearGradient id="colorApps" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                          <YAxis stroke="#94a3b8" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}
                            labelStyle={{ fontWeight: 'bold' }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="applications" 
                            stroke="#6366f1" 
                            strokeWidth={2}
                            fillOpacity={1} 
                            fill="url(#colorApps)" 
                            name="Bewerbungen"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Popular Categories */}
                <Card className="border-slate-100 shadow-sm rounded-2xl">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-outfit flex items-center gap-2">
                      <Tag className="w-5 h-5 text-primary" />
                      Beliebteste Kategorien
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={statistics.popularCategories} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                          <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={11} width={100} />
                          <Tooltip 
                            contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}
                          />
                          <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Jobs" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Location Stats */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Applications by State */}
                <Card className="border-slate-100 shadow-sm rounded-2xl">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-outfit flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      Bewerbungen nach Bundesland
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {statistics.applicationsByState.length > 0 ? (
                        statistics.applicationsByState.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="w-24 text-sm text-slate-600 truncate">{item.state}</div>
                            <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
                                style={{ 
                                  width: `${Math.min((item.count / Math.max(...statistics.applicationsByState.map(s => s.count))) * 100, 100)}%` 
                                }}
                              ></div>
                            </div>
                            <div className="w-8 text-sm font-semibold text-slate-700">{item.count}</div>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-500 text-center py-8">Keine Daten vorhanden</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Applications by District */}
                <Card className="border-slate-100 shadow-sm rounded-2xl">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-outfit flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      Top 10 Bezirke
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {statistics.applicationsByDistrict.length > 0 ? (
                        statistics.applicationsByDistrict.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="w-6 text-sm font-bold text-slate-400">#{idx + 1}</div>
                            <div className="flex-1 text-sm text-slate-700 truncate">{item.district}</div>
                            <Badge variant="secondary">{item.count}</Badge>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-500 text-center py-8">Keine Daten vorhanden</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <Card className="border-slate-100 shadow-sm rounded-2xl">
              <CardContent className="py-20 text-center">
                <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Klicke auf "Statistiken" um die Daten zu laden</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Settings View - Category Management */}
      {activeView === 'settings' && (
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold font-outfit flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Einstellungen
            </h2>
            <p className="text-slate-600">Verwalte Job-Kategorien und andere Einstellungen</p>
          </div>

          <Card className="border-slate-100 shadow-sm rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-outfit">
                <Tag className="w-5 h-5" />
                Job-Kategorien verwalten
              </CardTitle>
              <p className="text-sm text-slate-600">
                Diese Kategorien werden in der Job-Erstellung und im Student-Onboarding angezeigt
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add new category */}
              <div className="flex gap-2">
                <Input
                  placeholder="Neue Kategorie hinzufügen..."
                  value={settingsCategoryInput}
                  onChange={(e) => setSettingsCategoryInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddMasterCategory()}
                  className="flex-1 rounded-xl"
                  data-testid="settings-category-input"
                />
                <Button
                  onClick={handleAddMasterCategory}
                  className="rounded-xl"
                  data-testid="add-master-category-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Hinzufügen
                </Button>
              </div>

              {/* Category list */}
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Aktive Kategorien ({availableCategories.length})</Label>
                <div className="flex flex-wrap gap-2 p-4 bg-slate-50 rounded-xl min-h-[100px]">
                  {availableCategories.map((category) => (
                    <div
                      key={category}
                      className="bg-white border border-slate-200 px-3 py-2 rounded-lg flex items-center gap-2 shadow-sm group hover:border-red-300 transition-colors"
                    >
                      <Tag className="w-3 h-3 text-primary" />
                      <span className="text-sm font-medium">{category}</span>
                      <button
                        onClick={() => handleDeleteMasterCategory(category)}
                        className="text-slate-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                        data-testid={`delete-category-${category}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {availableCategories.length === 0 && (
                    <p className="text-slate-400 text-sm">Keine Kategorien vorhanden</p>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  <strong>Hinweis:</strong> Kategorien, die bereits in Jobs verwendet werden, werden auch dann noch angezeigt, 
                  wenn sie hier gelöscht werden. Beim Erstellen neuer Jobs sind nur die hier definierten Kategorien verfügbar.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* SMTP Settings */}
          <Card className="border-slate-100 shadow-sm rounded-2xl mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-outfit">
                <Mail className="w-5 h-5" />
                E-Mail Einstellungen (SMTP)
              </CardTitle>
              <p className="text-sm text-slate-600">
                Konfiguriere den E-Mail-Versand für Benachrichtigungen
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {smtpConfigured && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 text-sm font-medium">SMTP ist konfiguriert</span>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SMTP Server</Label>
                  <Input
                    placeholder="z.B. smtp.gmail.com"
                    value={smtpSettings.host}
                    onChange={(e) => setSmtpSettings({...smtpSettings, host: e.target.value})}
                    data-testid="smtp-host"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input
                    type="number"
                    placeholder="587"
                    value={smtpSettings.port}
                    onChange={(e) => setSmtpSettings({...smtpSettings, port: parseInt(e.target.value) || 587})}
                    data-testid="smtp-port"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Benutzername</Label>
                  <Input
                    placeholder="E-Mail-Adresse"
                    value={smtpSettings.username}
                    onChange={(e) => setSmtpSettings({...smtpSettings, username: e.target.value})}
                    data-testid="smtp-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Passwort</Label>
                  <Input
                    type="password"
                    placeholder={smtpConfigured ? "••••••••" : "Passwort eingeben"}
                    value={smtpSettings.password}
                    onChange={(e) => setSmtpSettings({...smtpSettings, password: e.target.value})}
                    data-testid="smtp-password"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Absender E-Mail</Label>
                  <Input
                    placeholder="noreply@example.com"
                    value={smtpSettings.from_email}
                    onChange={(e) => setSmtpSettings({...smtpSettings, from_email: e.target.value})}
                    data-testid="smtp-from-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Admin E-Mail (Empfänger)</Label>
                  <Input
                    placeholder="leitung@zoro-verein.at"
                    value={smtpSettings.admin_email}
                    onChange={(e) => setSmtpSettings({...smtpSettings, admin_email: e.target.value})}
                    data-testid="smtp-admin-email"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={handleSaveSmtpSettings} data-testid="save-smtp-button">
                  Speichern
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleTestSmtp}
                  disabled={!smtpConfigured || testingSmtp}
                  data-testid="test-smtp-button"
                >
                  {testingSmtp ? (
                    <>Sende...</>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Test-E-Mail senden
                    </>
                  )}
                </Button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                <p className="text-sm text-amber-800">
                  <strong>Sicherheitshinweis:</strong> Das Passwort wird verschlüsselt in der Datenbank gespeichert. 
                  Für mehr Sicherheit empfehlen wir die Verwendung eines App-spezifischen Passworts.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Email Templates */}
          <Card className="border-slate-100 shadow-sm rounded-2xl mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-outfit">
                <FileText className="w-5 h-5" />
                E-Mail-Vorlagen
              </CardTitle>
              <p className="text-sm text-slate-600">
                Bearbeite die automatischen E-Mail-Texte
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'registration', label: 'Registrierungs-Bestätigung', desc: 'Wird bei Registrierung gesendet' },
                { key: 'new_application', label: 'Neue Bewerbung (an Admin)', desc: 'Wenn ein Student sich bewirbt' },
                { key: 'application_accepted', label: 'Bewerbung angenommen', desc: 'Wird an Student gesendet' },
                { key: 'application_rejected', label: 'Bewerbung abgelehnt', desc: 'Wird an Student gesendet' }
              ].map((template) => (
                <div key={template.key} className="border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium">{template.label}</h4>
                      <p className="text-xs text-slate-500">{template.desc}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingTemplate(editingTemplate === template.key ? null : template.key)}
                      data-testid={`edit-template-${template.key}`}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      {editingTemplate === template.key ? 'Schließen' : 'Bearbeiten'}
                    </Button>
                  </div>
                  
                  {editingTemplate === template.key && (
                    <div className="space-y-3 mt-4 pt-4 border-t border-slate-200">
                      <div className="space-y-2">
                        <Label>Betreff</Label>
                        <Input
                          value={emailTemplates[template.key]?.subject || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            [template.key]: { ...emailTemplates[template.key], subject: e.target.value }
                          })}
                          placeholder="E-Mail Betreff"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Inhalt (HTML)</Label>
                        <Textarea
                          value={emailTemplates[template.key]?.body || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            [template.key]: { ...emailTemplates[template.key], body: e.target.value }
                          })}
                          placeholder="E-Mail Inhalt (HTML erlaubt)"
                          rows={8}
                          className="font-mono text-sm"
                        />
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg text-xs text-blue-800">
                        <strong>Verfügbare Platzhalter:</strong><br />
                        {template.key === 'registration' && '{name}, {confirm_url}'}
                        {template.key === 'new_application' && '{student_name}, {job_title}, {student_email}, {student_phone}'}
                        {template.key.includes('application_') && '{name}, {job_title}'}
                      </div>
                      <Button onClick={() => handleSaveEmailTemplate(template.key)}>
                        Vorlage speichern
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Propose Job Dialog */}
      <Dialog open={proposeOpen} onOpenChange={setProposeOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Job vorschlagen
            </DialogTitle>
            <DialogDescription>
              Schlage <strong>{proposeApplicant?.profile?.firstName} {proposeApplicant?.profile?.lastName}</strong> einen Job vor.
              Der Bewerber erhält eine E-Mail mit den Eckdaten und Sabines Telefonnummer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="propose-job-select">Job auswählen *</Label>
              <Select value={proposeJobId} onValueChange={setProposeJobId}>
                <SelectTrigger id="propose-job-select" data-testid="propose-job-select">
                  <SelectValue placeholder="Job wählen…" />
                </SelectTrigger>
                <SelectContent className="z-[100] max-h-72">
                  {jobs.filter(j => !j.archived).map((job) => (
                    <SelectItem key={job.jobId} value={job.jobId}>
                      {job.title} {job.location ? `· ${job.location}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="propose-message">Notiz (optional)</Label>
              <Textarea
                id="propose-message"
                value={proposeMessage}
                onChange={(e) => setProposeMessage(e.target.value)}
                placeholder="Persönliche Nachricht für den Bewerber – z.B. warum dieser Job passt…"
                rows={3}
                data-testid="propose-message-textarea"
              />
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-900">
              ⏱️ Der Vorschlag läuft automatisch nach 7 Tagen ab, falls der Bewerber nicht reagiert.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProposeOpen(false)} disabled={proposeLoading}>
              Abbrechen
            </Button>
            <Button onClick={handleSendProposal} disabled={proposeLoading || !proposeJobId} data-testid="send-proposal-button">
              {proposeLoading ? 'Wird versendet…' : 'Vorschlag senden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
